import React, { useEffect, useRef, useState } from 'react';

interface AutoplayVideoProps {
  src: string;
  poster?: string;
  className?: string;
  threshold?: number;
  fallbackSrc?: string;
}

export const AutoplayVideo: React.FC<AutoplayVideoProps> = ({
  src,
  poster,
  className = '',
  threshold = 0.3,
  fallbackSrc
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        
        if (entry.isIntersecting && !prefersReducedMotion) {
          video.play().catch(err => {
            console.log('Autoplay prevented:', err);
          });
        } else {
          video.pause();
        }
      },
      {
        threshold: threshold,
        rootMargin: '50px'
      }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  const handleError = () => {
    if (!hasError && fallbackSrc) {
      setHasError(true);
    }
  };

  return (
    <div className={`relative bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-xl sm:rounded-2xl overflow-hidden ${className}`}>
      {isLoading && poster && (
        <img 
          src={poster} 
          alt="Video poster" 
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        autoPlay={false}
        muted
        loop
        playsInline
        poster={poster}
        preload="metadata"
        onError={handleError}
        onLoadedData={() => setIsLoading(false)}
        onCanPlay={() => setIsLoading(false)}
      >
        <source src={hasError && fallbackSrc ? fallbackSrc : src} type={hasError ? "video/mp4" : "video/webm"} />
        {fallbackSrc && !hasError && (
          <source src={fallbackSrc} type="video/mp4" />
        )}
        Your browser does not support the video tag.
      </video>
      
      {!isVisible && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-300">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <div className="w-0 h-0 border-l-[14px] sm:border-l-[16px] border-l-white/80 border-y-[8px] sm:border-y-[10px] border-y-transparent ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};